import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Search, MapPin, Phone, Building2, Loader, Map as MapIcon, Crosshair } from 'lucide-react';
import PolicyNoticeBanner from './common/PolicyNoticeBanner';
import { POLICY_NOTICES, LBS_TERMS_V11_KEY } from '../config/policyNotices';
import LocationConsentModal from './common/LocationConsentModal';
import {
    LOCATION_SERVICES, readLocationConsent, writeLocationConsent, clearLocationConsent,
    LOCATION_REVOKE_OK, LOCATION_REVOKE_FAIL, LOCATION_REGION_HINT, truncCoord, locationUsePayload, locationErrorMessage,
} from '../utils/locationConsent';
import { isAppCheckError, APP_CHECK_REQUIRED_TEXT } from '../utils/appCheckError';

const LOC_SERVICE = LOCATION_SERVICES.findClinic.key;

/**
 * 전국 치과 검색 페이지 (공개)
 * - 카카오맵·카드 목록 모두 전체 치과를 동일한 모양으로 표시 (거리순)
 * - 의료법 정리(2026-09-15, docs/법무검토_치과지도_원장명함_2026-09-15.md):
 *   구독 여부에 따른 강조·정렬·배지, 별점·리뷰, 예약·가맹 유도 문구 제거.
 *   표시 = 치과명·주소·전화·진료과목·거리 등 사실 정보만
 * - patientSearchHospitals Cloud Function 사용 (위치 있으면 서버가 거리순 정렬)
 * - 2026-09-16 서버 검색 중립화(카카오 단일 원천, id = 'kakao:…'): /clinic-profile/:id 는 hospitals 문서를 읽으므로
 *   카카오 id 로는 열리지 않는다 → 카드·지도 팝업의 [상세] 링크 제거, 카드 안 정보(이름·주소·전화·출처)만 표시.
 *   출처는 서버 dataSource/source.label 그대로("카카오 장소 정보").
 */
function PublicClinicDirectory() {
    const [keyword, setKeyword] = useState('');
    const [allHospitals, setAllHospitals] = useState([]);   // 누적된 페이지 (지도·목록 공통)
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searched, setSearched] = useState(false);
    const [pageOffset, setPageOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const PAGE_SIZE = 30;  // PC 사양 — 한 번에 30개씩
    const [userLocation, setUserLocation] = useState(null); // {lat, lng}
    const [lastSearchCenter, setLastSearchCenter] = useState(null); // 마지막 검색한 중심점
    const [showResearchBtn, setShowResearchBtn] = useState(false);
    const [sourceLabel, setSourceLabel] = useState(DEFAULT_SOURCE_LABEL);   // 서버 source.label (중립화 계약)

    const RADIUS_KM = 10; // 검색 반경
    const RESEARCH_TRIGGER_KM = 5; // 이 거리 이상 이동하면 재검색 버튼 노출

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const userLocationMarkerRef = useRef(null);
    const [locating, setLocating] = useState(false);
    const [locationError, setLocationError] = useState('');

    // Haversine 거리 (km)
    const distanceKm = (lat1, lng1, lat2, lng2) => {
        const R = 6371;
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLng = (lng2 - lng1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    // 첫 진입: 위치 없이 일반 검색 (2026-09-16 대표 결정 — 진입 시 자동 위치 요청 제거, 위치는 [현재 위치로 찾기] 동의 후에만)
    useEffect(() => {
        search(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 위치 이용 안내 + 만 14세 확인 (cp-1.4 정본 부록 B-3). 결과는 이 브라우저 localStorage 에만 — 서버 전송 금지
    const [showLocConsent, setShowLocConsent] = useState(false);
    const [regionHint, setRegionHint] = useState('');
    const [revokeToast, setRevokeToast] = useState('');
    const [searchError, setSearchError] = useState('');

    // 위치 이용 동의 철회 (부록 D-1 3번) — 브라우저 동의 기록 삭제. 이후 [현재 위치로 찾기]를 누르면 안내창이 다시 뜬다
    const revokeLocConsent = () => {
        setRevokeToast(clearLocationConsent() ? LOCATION_REVOKE_OK : LOCATION_REVOKE_FAIL);
        window.setTimeout(() => setRevokeToast(''), 4000);
    };

    const onLocationButton = () => {
        setRegionHint('');
        if (readLocationConsent(LOC_SERVICE)) { moveToMyLocation(); return; }
        setShowLocConsent(true);
    };
    const agreeLocConsent = () => {
        writeLocationConsent(LOC_SERVICE);
        setShowLocConsent(false);
        moveToMyLocation();
    };
    const declineLocConsent = () => {
        setShowLocConsent(false);
        setRegionHint(LOCATION_REGION_HINT);
    };

    // 현재 지도 중심으로 재검색 — userLocation도 갱신해서 50km 필터 즉시 적용
    const searchAtMapCenter = () => {
        if (!mapInstance.current || !window.kakao?.maps) return;
        const center = mapInstance.current.getCenter();
        const newLoc = { lat: truncCoord(center.getLat()), lng: truncCoord(center.getLng()) };
        setUserLocation(newLoc);
        setShowResearchBtn(false);
        search(newLoc);
    };

    // 내 위치로 이동 + 주변 50km 재검색
    const moveToMyLocation = () => {
        if (!navigator.geolocation) {
            setLocationError('이 브라우저는 위치 정보를 지원하지 않습니다.');
            return;
        }
        setLocating(true);
        setLocationError('');
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const lat = truncCoord(pos.coords.latitude);
                const lng = truncCoord(pos.coords.longitude);
                const newLoc = { lat, lng };
                setUserLocation(newLoc);

                const kakao = window.kakao;
                if (kakao?.maps && mapInstance.current) {
                    const myCoords = new kakao.maps.LatLng(lat, lng);
                    mapInstance.current.setCenter(myCoords);
                    mapInstance.current.setLevel(7); // 50km 반경이 잘 보이도록
                    // 기존 위치 마커 제거 후 다시 그리기
                    if (userLocationMarkerRef.current) {
                        userLocationMarkerRef.current.setMap(null);
                    }
                    const myMarker = new kakao.maps.Marker({
                        map: mapInstance.current,
                        position: myCoords,
                        zIndex: 10,
                        image: new kakao.maps.MarkerImage(
                            'data:image/svg+xml;base64,' + btoa(`
                                <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                                    <circle cx='14' cy='14' r='12' fill='#ef4444' opacity='0.25' />
                                    <circle cx='14' cy='14' r='6' fill='#ef4444' stroke='#fff' stroke-width='2.5' />
                                </svg>`),
                            new kakao.maps.Size(28, 28),
                            { offset: new kakao.maps.Point(14, 14) }
                        ),
                    });
                    userLocationMarkerRef.current = myMarker;
                }
                // 주변 50km 재검색
                search(newLoc);
                setLocating(false);
            },
            (err) => {
                console.error('위치 가져오기 실패:', err?.code, err?.message);
                setLocationError(locationErrorMessage(err));
                setLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
        );
    };

    // 카카오맵 InfoWindow의 "전화" 버튼이 호출하는 글로벌 핸들러 — tel: 연결만(통화 추적 삭제, 대표 결정 2026-09-16)
    useEffect(() => {
        window.__dcCallClinic = (phone) => {
            if (!phone) return;
            window.location.href = `tel:${phone}`;
        };
        return () => { delete window.__dcCallClinic; };
    }, []);

    // 첫 페이지 검색 (새 위치/키워드 — replace)
    const search = async (locOverride) => {
        setLoading(true);
        setPageOffset(0);
        setHasMore(true);
        await fetchPage({ locOverride, replace: true, currentOffset: 0 });
        setLoading(false);
    };

    // 다음 페이지 (스크롤·"더 보기" — append)
    const loadMore = async () => {
        if (loadingMore || !hasMore || loading) return;
        setLoadingMore(true);
        await fetchPage({ replace: false, currentOffset: pageOffset });
        setLoadingMore(false);
    };

    const fetchPage = async ({ locOverride, replace, currentOffset }) => {
        const t0 = performance.now();
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const fn = httpsCallable(fns, 'patientSearchHospitals');
            const loc = locOverride !== undefined ? locOverride : userLocation;

            // 단일 호출 — 거리순(서버) + 30개씩 페이징. 구독 여부 우선 정렬 없음
            const res = await fn({
                subject: keyword.trim() || undefined,
                lat: loc?.lat != null ? truncCoord(loc.lat) : undefined,   // 지도 중심 재검색 포함 전송 전 절삭
                lng: loc?.lng != null ? truncCoord(loc.lng) : undefined,
                // 위치를 보낼 때만 동봉 — 서버 이용사실 기록용(좌표·식별자 없음, 서버 계약 2026-09-16)
                ...(loc?.lat != null && loc?.lng != null ? { locationUse: locationUsePayload() } : {}),
                limit: PAGE_SIZE,
                offset: currentOffset,
            });
            const t1 = performance.now();

            const newPage = (res.data?.hospitals || []).map(h => {
                const lat = h.lat ?? h.geoPoint?.lat;
                const lng = h.lng ?? h.geoPoint?.lng;
                if (loc?.lat && loc?.lng && lat != null && lng != null) {
                    return { ...h, _distanceKm: distanceKm(loc.lat, loc.lng, lat, lng) };
                }
                return h;
            });
            const responseHasMore = res.data?.hasMore === true;
            if (res.data?.source?.label) setSourceLabel(res.data.source.label);

            if (replace) {
                setAllHospitals(newPage);
                setPageOffset(newPage.length);
                if (loc?.lat && loc?.lng) {
                    setLastSearchCenter({ lat: loc.lat, lng: loc.lng });
                }
                setShowResearchBtn(false);
                setSearched(true);
            } else {
                const merged = [...allHospitals, ...newPage];
                setAllHospitals(merged);
                setPageOffset(currentOffset + newPage.length);
            }
            setHasMore(responseHasMore);
            console.log(`[치과찾기] +${newPage.length}곳 (offset=${currentOffset}, hasMore=${responseHasMore}) · ${Math.round(t1 - t0)}ms`);
            setSearchError('');
        } catch (e) {
            console.error('검색 실패:', e?.code || '', e?.message || e);
            setSearchError(isAppCheckError(e) ? APP_CHECK_REQUIRED_TEXT : '치과를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
        }
    };

    // 카카오맵 초기화 + 마커 표시
    const renderMap = useCallback(() => {
        if (!mapRef.current) return;
        const kakao = window.kakao;
        if (!kakao?.maps) return;

        const initMap = () => {
            if (!mapRef.current) return;
            const container = mapRef.current;
            // 첫 진입: userLocation 있으면 그 위치, 없으면 서울 시청
            const initialCenter = userLocation
                ? new kakao.maps.LatLng(userLocation.lat, userLocation.lng)
                : new kakao.maps.LatLng(37.5665, 126.9780);
            // 기존 인스턴스가 있으면 재사용 (재렌더 시 깜빡임 방지)
            const isFreshMap = !mapInstance.current;
            const map = mapInstance.current || new kakao.maps.Map(container, {
                center: initialCenter,
                level: 5,  // ~500m 반경 표시
            });
            mapInstance.current = map;
            // 새로 생성된 지도라면 사용자 위치 마커도 자동 표시
            if (isFreshMap && userLocation) {
                const myMarker = new kakao.maps.Marker({
                    map,
                    position: initialCenter,
                    zIndex: 10,
                    image: new kakao.maps.MarkerImage(
                        'data:image/svg+xml;base64,' + btoa(`
                            <svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 28 28'>
                                <circle cx='14' cy='14' r='12' fill='#ef4444' opacity='0.25' />
                                <circle cx='14' cy='14' r='6' fill='#ef4444' stroke='#fff' stroke-width='2.5' />
                            </svg>`),
                        new kakao.maps.Size(28, 28),
                        { offset: new kakao.maps.Point(14, 14) }
                    ),
                });
                userLocationMarkerRef.current = myMarker;
            }

            const geocoder = new kakao.maps.services.Geocoder();
            // 기존 마커 제거
            markersRef.current.forEach(m => m.setMap(null));
            markersRef.current = [];

            // 통합 popup 관리
            let activePopup = null;
            const closeActivePopup = () => {
                if (activePopup) { activePopup.close(); activePopup = null; }
            };
            kakao.maps.event.addListener(map, 'click', closeActivePopup);

            // 지도 이동 종료 → "현재 지도에서 검색" 버튼 노출 (거리 비교 없이 항상)
            kakao.maps.event.addListener(map, 'idle', () => {
                setShowResearchBtn(true);
            });

            // 모든 치과 동일 마커·동일 팝업 (구독 여부로 구분하지 않음)
            const drawMarker = (h, lat, lng) => {
                const coords = new kakao.maps.LatLng(lat, lng);
                // Unicode-safe SVG (한글 포함) — encodeURIComponent 방식으로 base64 인코딩
                const svgFree = `<svg xmlns='http://www.w3.org/2000/svg' width='26' height='26' viewBox='0 0 26 26'><circle cx='13' cy='13' r='10' fill='#3b82f6' stroke='#fff' stroke-width='2.5' opacity='0.92'/><circle cx='13' cy='13' r='3.5' fill='#fff'/></svg>`;
                const dotImage = new kakao.maps.MarkerImage(
                    'data:image/svg+xml;base64,' + btoa(svgFree),
                    new kakao.maps.Size(26, 26),
                    { offset: new kakao.maps.Point(13, 13) }
                );
                const marker = new kakao.maps.Marker({ map, position: coords, image: dotImage, zIndex: 2 });
                markersRef.current.push(marker);

                // 팝업은 HTML 문자열이라 외부(카카오·HIRA·치과 입력) 값은 반드시 이스케이프 (2026-09-15 보안)
                const esc = (v) => String(v == null ? '' : v).replace(/[&<>"'`]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' }[c]));
                const jsArg = (v) => String(v == null ? '' : v).replace(/[^0-9A-Za-z_:\-+.]/g, ''); // onclick 인자: id·전화번호에 필요한 문자만
                const phone = (h.phone || '').toString().trim();
                const phoneRow = phone
                    ? `<div style="margin-top:8px;display:flex;align-items:center;justify-content:space-between;gap:8px;">
                            <span style="color:#0f172a;font-size:12px;font-weight:700;white-space:nowrap;">📞 ${esc(phone)}</span>
                            <button onclick="window.__dcCallClinic && window.__dcCallClinic('${jsArg(phone)}')"
                                style="padding:5px 12px;background:#10b981;color:#fff;border:none;border-radius:6px;font-size:11px;font-weight:700;cursor:pointer;white-space:nowrap;">
                                전화
                            </button>
                       </div>`
                    : '';

                const content = `
                    <div style="padding:12px 12px 10px 12px;font-size:12px;width:240px;line-height:1.5;font-family:'Apple SD Gothic Neo',sans-serif;word-break:keep-all;overflow-wrap:break-word;white-space:normal;box-sizing:border-box;">
                        <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;padding-right:18px;">
                            <strong style="font-size:13px;color:#0f172a;flex:1;">${esc(h.name || '치과')}</strong>
                        </div>
                        <div style="color:#64748b;font-size:11px;line-height:1.5;">${esc(h.address)}</div>
                        ${phoneRow}
                        <div style="margin-top:8px;color:#94a3b8;font-size:10px;">출처: ${esc(sourceLabelOf(h))}</div>
                    </div>`;
                const popup = new kakao.maps.InfoWindow({ content, removable: true });
                kakao.maps.event.addListener(marker, 'click', () => {
                    closeActivePopup();
                    popup.open(map, marker);
                    activePopup = popup;
                });
            };

            // 마커 그리기 — geoPoint(lat/lng)만 사용 (주소 지오코딩은 너무 느려 제외)
            const drawHospital = (h) => {
                const lat = h.lat ?? h.geoPoint?.lat;
                const lng = h.lng ?? h.geoPoint?.lng;
                if (lat == null || lng == null) return;
                drawMarker(h, lat, lng);
            };

            // 통계: 좌표 보유 vs 주소만 vs 둘 다 없음
            let withGeo = 0, withAddrOnly = 0, withNothing = 0;
            allHospitals.forEach(h => {
                if ((h.lat && h.lng) || (h.geoPoint?.lat && h.geoPoint?.lng)) withGeo++;
                else if (h.address) withAddrOnly++;
                else withNothing++;
                drawHospital(h);
            });
            console.log(`[치과찾기] 전체 ${allHospitals.length}곳 → 좌표보유 ${withGeo} · 주소만(스킵) ${withAddrOnly} · 데이터없음 ${withNothing}`);

            // 첫 진입(isFreshMap)일 때만 자동 fitBounds — 마커 모두 보이게 자동 조정
            if (isFreshMap && allHospitals.length > 0) {
                const bounds = new kakao.maps.LatLngBounds();
                let added = 0;
                allHospitals.forEach(h => {
                    const lat = h.lat ?? h.geoPoint?.lat;
                    const lng = h.lng ?? h.geoPoint?.lng;
                    if (lat != null && lng != null) {
                        bounds.extend(new kakao.maps.LatLng(lat, lng));
                        added++;
                    }
                });
                // 사용자 위치도 포함
                if (userLocation) {
                    bounds.extend(new kakao.maps.LatLng(userLocation.lat, userLocation.lng));
                }
                if (added > 0) {
                    map.setBounds(bounds);
                    // 너무 줌인되면 적절히 줌아웃 (최소 level 4 = ~250m 그리드)
                    if (map.getLevel() < 4) map.setLevel(4);
                }
            }
        };

        if (kakao.maps.services) {
            initMap();
        } else {
            kakao.maps.load(initMap);
        }
    }, [allHospitals]);

    useEffect(() => {
        if (!loading && allHospitals.length > 0) {
            // SDK 로딩 대기
            const tryRender = (attempt = 0) => {
                if (window.kakao?.maps) {
                    renderMap();
                } else if (attempt < 20) {
                    setTimeout(() => tryRender(attempt + 1), 200);
                }
            };
            tryRender();
        }
    }, [loading, allHospitals, renderMap]);

    return (
        <div style={S.container}>
            {/* 구 환자앱 종료 고지 — config/policyNotices 문서 없으면 미표시 */}
            <PolicyNoticeBanner />
            {/* 위치약관 v1.1 공고(2026-09-16 ~ 시행일) — 닫으면 이 브라우저에서만 숨김 */}
            <PolicyNoticeBanner noticeKey={LBS_TERMS_V11_KEY} fallback={POLICY_NOTICES[LBS_TERMS_V11_KEY]} dismissible />
            <div style={S.header}>
                <h1 style={S.title}><Building2 size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} /> 치과 찾기</h1>
                <p style={S.subtitle}>전국 치과 검색 · 건강보험심사평가원 등록 정보 기준</p>
            </div>

            <div style={S.searchCard}>
                <div style={{ display: 'flex', gap: 8 }}>
                    <input
                        type="text"
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && search()}
                        placeholder="치과명 · 지역 · 시군구 (예: 김포시, 강남구)"
                        style={S.input}
                    />
                    <button onClick={search} disabled={loading} style={S.btn}>
                        {loading ? <Loader size={14} className="spin" /> : <Search size={14} />} 검색
                    </button>
                </div>
            </div>

            {/* 카카오맵 */}
            <div style={S.mapWrap}>
                <div style={S.mapHeader}>
                    <MapIcon size={16} color="#10b981" />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#065f46' }}>지도에서 보기</span>
                    <button
                        onClick={onLocationButton}
                        disabled={locating}
                        style={{
                            marginLeft: 'auto',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '6px 12px',
                            background: locating ? '#cbd5e1' : '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: locating ? 'wait' : 'pointer',
                        }}
                        title="현재 위치 기준으로 가까운 치과 찾기"
                    >
                        {locating ? <Loader size={12} className="spin" /> : <Crosshair size={12} />}
                        현재 위치로 찾기
                    </button>
                </div>
                {regionHint && (
                    <div style={{ padding: '8px 14px', background: '#f0f9ff', color: '#075985', fontSize: 12, borderBottom: '1px solid #bae6fd' }}>
                        {regionHint}
                    </div>
                )}
                {locationError && (
                    <div style={{ padding: '8px 14px', background: '#fef2f2', color: '#991b1b', fontSize: 11, borderBottom: '1px solid #fecaca' }}>
                        ⚠️ {locationError}
                    </div>
                )}
                <div style={{ position: 'relative' }}>
                    <div ref={mapRef} style={S.map} />
                    {showResearchBtn && (
                        <button
                            onClick={searchAtMapCenter}
                            disabled={loading}
                            style={{
                                position: 'absolute',
                                top: 12,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 100,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '8px 16px',
                                background: '#fff',
                                color: '#10b981',
                                border: '2px solid #10b981',
                                borderRadius: 999,
                                fontSize: 13,
                                fontWeight: 800,
                                cursor: loading ? 'wait' : 'pointer',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {loading ? <Loader size={14} className="spin" /> : <Search size={14} />}
                            현재 지도에서 검색
                        </button>
                    )}
                </div>
            </div>

            {/* 위치정보법 약관 게시 — 지도·내 위치·거리 기능에 적용 (사용 시점 동의, 2026-08-06) */}
            <div style={{ textAlign: 'right', margin: '6px 4px 0' }}>
                <a href="/location-terms" target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 11, color: '#94a3b8', textDecoration: 'underline' }}>
                    위치기반서비스 이용약관
                </a>
                <button type="button" onClick={revokeLocConsent}
                    style={{ marginLeft: 10, padding: 0, background: 'none', border: 'none', fontSize: 11, color: '#94a3b8', textDecoration: 'underline', cursor: 'pointer' }}>
                    위치 이용 동의 철회
                </button>
            </div>
            {revokeToast && (
                <div role="status" style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 2100, background: '#0f172a', color: '#fff', padding: '10px 16px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', maxWidth: '90vw' }}>
                    {revokeToast}
                </div>
            )}

            {searchError && (
                <div style={{ margin: '0 0 12px', padding: '10px 14px', background: '#fef2f2', color: '#991b1b', fontSize: 13, borderRadius: 8, border: '1px solid #fecaca' }}>{searchError}</div>
            )}

            {/* 위치 이용 안내 — 공용 컴포넌트(문구 정본: 약관 v1.1 게시본 §5) */}
            {showLocConsent && (
                <LocationConsentModal title="현재 위치로 치과 찾기" onAgree={agreeLocConsent} onDecline={declineLocConsent} />
            )}

            {/* 치과 카드 목록 — 전체 치과, 동일 카드, 거리순 */}
            {loading ? (
                <div style={S.center}>로딩 중...</div>
            ) : !searched ? (
                <div style={S.empty}>치과를 검색해보세요</div>
            ) : (
                <div style={S.list}>
                    <div style={S.listHeader}>
                        <p style={{ fontSize: 13, color: '#0f172a', fontWeight: 700, margin: 0 }}>
                            치과 <span style={{ color: '#10b981' }}>{allHospitals.length}곳</span>
                            <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 8 }}>
                                {userLocation ? `· 반경 ${RADIUS_KM}km 내 · 가까운 순` : ''}
                            </span>
                        </p>
                    </div>
                    {allHospitals.length === 0 ? (
                        <div style={S.empty}>검색 결과가 없습니다.</div>
                    ) : (
                        sortByDistance(allHospitals).map((h, i) => <ClinicCard key={h.id || i} h={h} sourceLabel={sourceLabel} />)
                    )}
                    {/* 더 보기 버튼 (페이징) */}
                    {hasMore && allHospitals.length > 0 && (
                        <div style={{ textAlign: 'center', padding: '16px 0' }}>
                            <button
                                onClick={loadMore}
                                disabled={loadingMore}
                                style={{
                                    padding: '12px 32px',
                                    background: '#fff', color: '#0ea5e9',
                                    border: '1.5px solid #0ea5e9', borderRadius: 8,
                                    fontSize: 13, fontWeight: 800, cursor: loadingMore ? 'wait' : 'pointer',
                                }}
                            >
                                {loadingMore ? '불러오는 중...' : `+ ${PAGE_SIZE}곳 더 보기`}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// 데이터 출처 표시 — 항목별 dataSource 코드 → 사실 표기(서버 SEARCH_DATA_SOURCE 와 동기). 코드가 없으면 기본 라벨
const DEFAULT_SOURCE_LABEL = '카카오 장소 정보';
const SOURCE_LABELS = { kakao_local: DEFAULT_SOURCE_LABEL };
function sourceLabelOf(h, fallback = DEFAULT_SOURCE_LABEL) {
    return SOURCE_LABELS[h?.dataSource] || fallback;
}

// 거리순 정렬 (거리 없는 항목은 뒤로, 원래 순서 유지) — 구독 여부는 정렬에 쓰지 않음
function sortByDistance(list) {
    return list
        .map((h, idx) => ({ h, idx }))
        .sort((a, b) => {
            const da = typeof a.h._distanceKm === 'number' ? a.h._distanceKm : Infinity;
            const dbb = typeof b.h._distanceKm === 'number' ? b.h._distanceKm : Infinity;
            if (da !== dbb) return da - dbb;
            return a.idx - b.idx;
        })
        .map(x => x.h);
}

// 모든 치과 동일 카드 — 사실 정보(치과명·주소·전화·진료과목·거리)만
function ClinicCard({ h, sourceLabel }) {
    const subjects = Array.isArray(h.subjects) ? h.subjects.filter(Boolean) : [];
    return (
        <div style={S.card}>
            <div style={{ flex: 1 }}>
                <div style={S.cardHead}>
                    <h3 style={{ ...S.cardTitle, color: '#0f172a' }}>{h.name || '치과'}</h3>
                    {typeof h._distanceKm === 'number' && (
                        <span style={S.distancePill}>
                            {h._distanceKm < 1 ? `${Math.round(h._distanceKm * 1000)}m` : `${h._distanceKm.toFixed(1)}km`}
                        </span>
                    )}
                </div>
                {h.address && (
                    <div style={S.cardRow}>
                        <MapPin size={13} color="#64748b" />
                        <span>{h.address}</span>
                    </div>
                )}
                {h.phone && (
                    <div style={S.cardRow}>
                        <Phone size={13} color="#64748b" />
                        <span
                            onClick={(e) => {
                                e.stopPropagation();
                                if (window.__dcCallClinic) window.__dcCallClinic(h.phone);
                                else window.location.href = `tel:${h.phone}`;
                            }}
                            style={{ ...S.phoneLink, cursor: 'pointer' }}
                        >
                            {h.phone}
                        </span>
                    </div>
                )}
                {subjects.length > 0 && (
                    <div style={{ ...S.cardRow, flexWrap: 'wrap' }}>
                        <span>{'진료과목: ' + subjects.join(', ')}</span>
                    </div>
                )}
            </div>
                <div style={{ ...S.cardRow, fontSize: 11, color: '#94a3b8', marginBottom: 0 }}>
                    <span>{'출처: ' + sourceLabelOf(h, sourceLabel)}</span>
                </div>
        </div>
    );
}

const S = {
    container: { padding: 24, maxWidth: 1100, margin: '0 auto', fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    header: { marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 900, margin: 0 },
    subtitle: { fontSize: 14, color: '#64748b', marginTop: 4 },
    searchCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 16 },
    input: { flex: 1, padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 },
    btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    mapWrap: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', marginBottom: 20 },
    mapHeader: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    map: { width: '100%', height: 420 },
    list: { display: 'flex', flexDirection: 'column', gap: 12 },
    listHeader: { padding: '8px 0', marginBottom: 4 },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'flex-start', gap: 12 },
    cardHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
    cardTitle: { fontSize: 15, fontWeight: 800, margin: 0 },
    cardRow: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#64748b', marginBottom: 4 },
    phoneLink: { color: '#6366f1', fontWeight: 700, textDecoration: 'none' },
    distancePill: { background: '#e0e7ff', color: '#4338ca', padding: '2px 7px', borderRadius: 8, fontSize: 11, fontWeight: 700 },
    center: { padding: 60, textAlign: 'center', color: '#64748b' },
    empty: { padding: 40, textAlign: 'center', color: '#94a3b8', background: '#fff', borderRadius: 12, border: '1px dashed #e2e8f0' },
};

export default PublicClinicDirectory;
